import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MJNotificationService } from '@memberjunction/ng-notifications';
import { ResearchApiService } from './research-api.service';
import { AGURenderService } from 'agu_app_shell';
import { ItemKind, ResearchItemState } from '../models/research.models';

/** One item's save/pin/dismiss state changing, broadcast so every card showing the same item agrees. */
export interface MemberItemStateChange {
    kind: ItemKind;
    key: string;
    itemState: ResearchItemState;
    /**
     * False for the optimistic write, true once the server has answered (with
     * its own truth, or with the rolled-back previous state after a failure).
     *
     * Every toggle therefore emits TWICE. Subscribers that only repaint a card
     * want both — that is what makes the optimistic write feel instant. A
     * subscriber that reacts by RE-FETCHING a list must wait for the confirmed
     * emission, for two reasons: reloading twice is a wasted round trip (live
     * finding #18 — Undo fired two identical `ResearchGrantSearch` calls), and a
     * reload issued off the optimistic emission races the write it is reacting
     * to, so the server may still answer from the pre-toggle state.
     */
    confirmed: boolean;
    /**
     * True when this change took the item out of `dismissed` (an Undo of "Not for me"). The only change a finder re-fetches its list
     * for when it does not hold the item: a save, pin or Saved-board Undo elsewhere never re-runs another module's search (F-11).
     */
    undismissed: boolean;
}

/** The Undo affordance's payload — `MJNotificationService` has no action-button API (verified against node_modules/@memberjunction/ng-notifications/dist/lib/notifications.service.d.ts), so pages render this inline instead. */
export interface LastDismissedItem {
    kind: ItemKind;
    key: string;
    title: string;
}

const NO_STATE: ResearchItemState = { saved: false, pinned: false, dismissed: false };

/** How long the inline "Hidden: <title> — Undo" alert stays up before it self-clears. */
const UNDO_WINDOW_MS = 6000;

function CacheKey(kind: ItemKind, key: string): string {
    return `${kind}:${key}`;
}

/**
 * Single source of truth for a member's Save / Pin / "Not for me" state across
 * every card, list and the Home strip that shows a given (kind, key) item — a
 * toggle anywhere is reflected everywhere without a full re-fetch.
 *
 * Provided by RaSurfaceComponent (its element injector, not the root) — same
 * choice as RaCompositionState and ResearchApiService.
 */
@Injectable()
export class MemberItemStateService implements OnDestroy {
    private readonly cache = new Map<string, ResearchItemState>();
    private readonly changes = new Subject<MemberItemStateChange>();
    private readonly lastDismissed = new BehaviorSubject<LastDismissedItem | null>(null);
    private undoTimer: ReturnType<typeof setTimeout> | null = null;

    /** Emits whenever any item's state changes — optimistic write, server truth, or a reverted failure. */
    readonly Changes$: Observable<MemberItemStateChange> = this.changes.asObservable();

    /** The most recently dismissed item still inside its Undo window, or null. Pages render the inline "Hidden: <title> — Undo" alert from this. */
    readonly LastDismissed$: Observable<LastDismissedItem | null> = this.lastDismissed.asObservable();

    constructor(
        private readonly api: ResearchApiService,
        private readonly render: AGURenderService
    ) {}

    /**
     * Clears the 6 s undo timer if one is armed — mirrors SavedPageComponent's ngOnDestroy
     * (saved-page.component.ts:185-187). This service is provided by RaSurfaceComponent, so
     * Angular calls this when that surface is destroyed; a surface torn down mid-window would
     * otherwise leave the timer armed to fire `this.render.Refresh()` against a detached AGURenderService.
     */
    ngOnDestroy(): void {
        if (this.undoTimer !== null) clearTimeout(this.undoTimer);
    }

    /** Cache-first read; falls back to the caller's own value (e.g. the state a search result arrived with) when nothing has toggled yet this session. */
    StateOf(kind: ItemKind, key: string, fallback: ResearchItemState): ResearchItemState {
        return this.cache.get(CacheKey(kind, key)) ?? fallback;
    }

    /** Seeds the cache from a list of freshly-fetched items without emitting — a list re-render should not itself look like a toggle. */
    Prime(kind: ItemKind, items: ReadonlyArray<{ key: string; itemState: ResearchItemState }>): void {
        for (const item of items) {
            this.cache.set(CacheKey(kind, item.key), item.itemState);
        }
    }

    /**
     * Optimistic write: applies `patch` to the cache and emits immediately, then
     * confirms against the server. On failure the cache and every subscriber are
     * rolled back to the pre-toggle state and a toast reports the failure.
     */
    async Toggle(kind: ItemKind, key: string, patch: { saved?: boolean; pinned?: boolean; dismissed?: boolean }): Promise<ResearchItemState> {
        const cacheKey = CacheKey(kind, key);
        const previous = this.cache.get(cacheKey) ?? NO_STATE;
        const optimistic: ResearchItemState = { ...previous, ...patch };
        this.cache.set(cacheKey, optimistic);
        this.changes.next({ kind, key, itemState: optimistic, confirmed: false, undismissed: previous.dismissed && !optimistic.dismissed });

        try {
            const serverTruth = await this.api.SetItemState(kind, key, patch);
            this.cache.set(cacheKey, serverTruth);
            this.changes.next({ kind, key, itemState: serverTruth, confirmed: true, undismissed: previous.dismissed && !serverTruth.dismissed });
            return serverTruth;
        } catch (error) {
            this.cache.set(cacheKey, previous);
            this.changes.next({ kind, key, itemState: previous, confirmed: true, undismissed: false });
            MJNotificationService.Instance.CreateSimpleNotification("Couldn't save that change", 'error', 4000);
            throw error;
        }
    }

    /**
     * Marks an item dismissed and opens a 6 s inline Undo window (`LastDismissed$`).
     * The finder pages render "Hidden: <title>" with an Undo button that calls
     * `Toggle(kind, key, { dismissed: false })`.
     */
    async Dismiss(kind: ItemKind, key: string, title: string): Promise<void> {
        await this.Toggle(kind, key, { dismissed: true });
        if (this.undoTimer !== null) clearTimeout(this.undoTimer);
        this.lastDismissed.next({ kind, key, title });
        this.undoTimer = setTimeout(() => {
            this.undoTimer = null;
            this.lastDismissed.next(null);
            // A timer firing is not a user event and not an API call, so it is
            // the one other place this zoneless host needs telling to repaint.
            this.render.Refresh();
        }, UNDO_WINDOW_MS);
    }

    /** Undoes the current `LastDismissed$` item, if its window is still open. */
    async Undo(): Promise<void> {
        const current = this.lastDismissed.value;
        if (!current) return;
        if (this.undoTimer !== null) {
            clearTimeout(this.undoTimer);
            this.undoTimer = null;
        }
        this.lastDismissed.next(null);
        await this.Toggle(current.kind, current.key, { dismissed: false });
    }
}
